import { Loading } from "@/components";
import {
    AlertDialog,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
} from "@/components/ui/alert-dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { cn } from "@/services/hooks/utils";
import type { BoxItem } from "@/services/types";
import { ArrowRightIcon } from "lucide-react";
import { ArrowLeftIcon } from "lucide-react";
import { useState } from "react";
import { pastBoxesQuery } from "./queries";

type PastBoxesProps = {
    subscriptionId: number;
    onClose: () => void;
};

const PastBoxes = ({ subscriptionId, onClose }: PastBoxesProps) => {
    const [week, setWeek] = useState(1);
    const firstWeekQuery = pastBoxesQuery({
        subscriptionId,
        week: 1,
    });
    const secondWeekQuery = pastBoxesQuery({
        subscriptionId,
        week: 2,
    });
    const activeQuery = week === 1 ? firstWeekQuery : secondWeekQuery;
    const data = activeQuery.data?.data;
    const isLoading = activeQuery.isLoading;

    const renderProductRows = (products: BoxItem[]) => {
        if (products.length === 0) {
            return (
                <TableRow>
                    <TableCell
                        colSpan={3}
                        className="py-6 text-center text-sm text-muted-foreground"
                    >
                        No hay productos para esta semana.
                    </TableCell>
                </TableRow>
            );
        }

        return products.map((p) => (
            <TableRow key={p.id}>
                <TableCell className="text-sm text-muted-foreground">
                    {p.full_product_name}
                </TableCell>
                <TableCell className="whitespace-nowrap text-sm font-semibold text-slate-800">
                    {p.quantity} {p.quantity_unit_type}
                </TableCell>
                <TableCell className="text-right text-sm font-semibold">
                    {p.box_has_change_label ?? (p.box_has_change ? "si" : "no")}
                </TableCell>
            </TableRow>
        ));
    };

    const renderContent = () => {
        return (
            <AlertDialogHeader>
                <AlertDialogDescription>
                    <div className="p-2 border-t border-l border-r rounded-t-md rounded-b-none bg-primary text-white flex justify-between items-center">
                        <p>
                            {isLoading ? (
                                "Caja"
                            ) : (
                                <span>
                                    <strong className="font-semibold">
                                        {data?.subscription_name}
                                    </strong>{" "}
                                    de hace{" "}
                                    <strong className="font-semibold">
                                        {week}{" "}
                                        {week === 1 ? "semana" : "semanas"}
                                    </strong>
                                </span>
                            )}
                        </p>
                        <div className="flex items-center gap-2">
                            <button
                                aria-label="Semana anterior"
                                type="button"
                                onClick={() =>
                                    setWeek((w) => Math.min(w + 1, 2))
                                }
                                disabled={week === 2}
                                className={cn(
                                    "text-white cursor-pointer",
                                    week === 2 && "opacity-50",
                                )}
                            >
                                <ArrowLeftIcon className="w-4 h-4" />
                            </button>
                            <button
                                aria-label="Semana siguiente"
                                type="button"
                                onClick={() =>
                                    setWeek((w) => Math.max(w - 1, 1))
                                }
                                disabled={week === 1}
                                className={cn(
                                    "text-white cursor-pointer",
                                    week === 1 && "opacity-50",
                                )}
                            >
                                <ArrowRightIcon className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                    <div className="relative">
                        <ScrollArea className="h-[300px] border rounded-md p-2">
                            <Table>
                                <TableHeader>
                                    <TableRow className="bg-slate-50">
                                        <TableHead>Producto</TableHead>
                                        <TableHead className="w-[95px]">
                                            Cantidad
                                        </TableHead>
                                        <TableHead className="w-[120px] text-right">
                                            Caja con cambio
                                        </TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {renderProductRows(data?.products ?? [])}
                                </TableBody>
                            </Table>
                        </ScrollArea>
                        {isLoading && (
                            <div className="absolute inset-0 bg-white/20 flex items-center justify-center">
                                <div>
                                    <Loading message="Buscando información de cajas..." />
                                </div>
                            </div>
                        )}
                    </div>
                </AlertDialogDescription>
            </AlertDialogHeader>
        );
    };

    return (
        <AlertDialog open>
            <AlertDialogContent>
                <div>
                    <h5 className="text-lg font-semibold mb-0">
                        Cajas de las ultimas 2 semanas
                    </h5>
                    <p className="text-sm text-muted-foreground">
                        Revisa el contenido histórico separado por producto,
                        cantidad y si la caja tuvo cambio.
                    </p>
                </div>
                {renderContent()}
                <AlertDialogFooter>
                    <AlertDialogCancel onClick={onClose}>
                        Cerrar
                    </AlertDialogCancel>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
};

export default PastBoxes;
